<script setup lang="ts">
/**
 * Settings > Order settings, read-only. Xobo staff set them in Mission
 * Control; the customer sees what applies to them here and how to ask for a
 * change. Values from GET /account/settings (company from the token). The
 * sign-in code, which the customer can change, is on Settings > OTP
 * settings (OtpSettings.vue).
 *
 * The drop-off limit is not shown: Xobo sets it per customer on request, and
 * customers are not told the number (the order form just stops offering
 * another stop). SMS updates are switched by Xobo only, so the page says how
 * to ask. Admins only (router ADMIN_ONLY_PATH_PREFIXES).
 */
import { onMounted, ref } from "vue";
import axios from "axios";
import { authHeaders, reasonFrom } from "../../composables/useCompanyKyc";

interface Settings {
  sms_order_updates: boolean;
}

const settings = ref<Settings | null>(null);
const loading = ref(true);
const error = ref("");
const hotline = import.meta.env.VITE_HOTLINE;

onMounted(async () => {
  try {
    const res = await axios.get(`${import.meta.env.VITE_ONBOARDING_SERVICE}account/settings`, {
      headers: authHeaders(),
    });
    settings.value = res.data?.data || null;
  } catch (err) {
    error.value = reasonFrom(err, "Could not load your order settings.");
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="ac-card" v-loading="loading">
    <div class="ac-panel-head">
      <div>
        <h2 class="ac-panel-title">Order settings</h2>
        <p class="ac-panel-desc">How orders on your account are handled</p>
      </div>
    </div>

    <div class="ac-card-body">
      <div v-if="error" class="ac-error">{{ error }}</div>

      <template v-else-if="settings">
        <div class="ac-section-label">Notifications</div>
        <div class="os-row">
          <div class="os-row__text">
            <div class="os-row__title">SMS updates from pickup to delivery</div>
            <p class="os-row__desc">
              Text messages as your orders move from pickup to delivery.
              <template v-if="settings.sms_order_updates">SMS sending is not live yet; this takes effect once it is.</template>
              <template v-else>Want them? Contact us and we'll turn them on.</template>
            </p>
          </div>
          <span class="ac-pill" :class="settings.sms_order_updates ? 'ac-pill--ok' : 'ac-pill--muted'">
            {{ settings.sms_order_updates ? "On" : "Off" }}
          </span>
        </div>

        <div class="ac-actions">
          <p class="ac-note">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
            <span>
              Xobo manages SMS updates for your account.
              <template v-if="hotline">To turn them on or off, call us on {{ hotline }}.</template>
              <template v-else>To turn them on or off, contact Xobo support.</template>
            </span>
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.os-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 4px 0 22px;
}
.os-row__text {
  min-width: 0;
  max-width: 60ch;
}
.os-row__title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #12284a;
}
.os-row__desc {
  margin: 4px 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #64748b;
}
@media (max-width: 640px) {
  .os-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
